import { createFileRoute, Link } from "@tanstack/react-router";
import { AuthSplitShell } from "@/components/auth/AuthSplitShell";
import { Heart, Stethoscope, ShieldCheck, ArrowRight } from "lucide-react";
import { brandedTitle, safeDesc } from "@/lib/seo-meta";

const META_TITLE = brandedTitle("Sign In");
const META_DESC = safeDesc(
  "Sign in to your Heallexa account as a patient, provider, or admin to manage your profile, listings, and appointments."
);

export const Route = createFileRoute("/login/")({
  head: () => ({
    meta: [
      { title: META_TITLE },
      { name: "description", content: META_DESC },
      { name: "robots", content: "noindex, follow" },
      { property: "og:title", content: META_TITLE },
      { property: "og:description", content: META_DESC },
      { property: "og:url", content: "https://heallexa.com/login" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://heallexa.com/login" }],
  }),
  component: LoginSelector,
});

function LoginSelector() {
  const opts = [
    { to: "/login/patient", icon: Heart, label: "Login as Patient", desc: "Find providers, save favorites, book visits.", color: "text-rose-500" },
    { to: "/login/provider", icon: Stethoscope, label: "Login as Provider", desc: "Claim and manage your practice listing.", color: "text-primary" },
    { to: "/login/admin", icon: ShieldCheck, label: "Login as Admin", desc: "Restricted access for platform admins.", color: "text-amber-600" },
  ] as const;

  return (
    <AuthSplitShell>
      <p className="text-[11px] font-semibold text-primary tracking-[0.18em] uppercase mb-2">
        Account access
      </p>
      <h1 className="text-2xl font-semibold tracking-tight">Sign in to your account</h1>
      <p className="mt-1 text-[13px] text-muted-foreground">Choose how you want to sign in.</p>

      <div className="mt-6 space-y-3">
        {opts.map(({ to, icon: Icon, label, desc, color }) => (
          <Link
            key={to}
            to={to}
            className="card-surface-flat hover-lift group flex items-center gap-4 rounded-md p-4"
          >
            <div className={`h-11 w-11 shrink-0 rounded-md border border-border bg-muted/60 inline-flex items-center justify-center ${color}`}>
              <Icon className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <p className="text-[15px] font-semibold leading-tight">{label}</p>
              <p className="mt-0.5 text-[13px] text-muted-foreground">{desc}</p>
            </div>
            <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
          </Link>
        ))}
      </div>

      <p className="mt-6 text-center text-xs text-muted-foreground">
        <Link to="/" className="hover:text-foreground">← Back to home</Link>
      </p>
    </AuthSplitShell>
  );
}
